import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from '@/components/ui/card';
import { X, Plus } from 'lucide-react';
import type { ParsedJobData, CreateJobProfileData } from '@/types/jobProfile';

interface EditableFieldsProps {
 parsedData: ParsedJobData;
 onSave: (data: CreateJobProfileData) => void;
 onBack: () => void;
}

export function EditableFields({
 parsedData,
 onSave,
 onBack,
}: EditableFieldsProps) {
 const [formData, setFormData] = useState<CreateJobProfileData>({
  jobTitle: parsedData.jobTitle || '',
  companyName: parsedData.companyName || '',
  jobDescription: parsedData.jobDescription || '',
  companyInfo: parsedData.companyInfo || '',
  interviewType: 'technical',
  experienceLevel: parsedData.experienceLevel || 'mid',
  industry: parsedData.industry || '',
  skills: parsedData.skills || [],
 });

 const [newSkill, setNewSkill] = useState('');
 const [extraContext, setExtraContext] = useState('');

 // Additional parsed fields for display
 const [additionalInfo] = useState({
  yearsOfExperience: parsedData.yearsOfExperience || '',
  workModel: parsedData.workModel || '',
  companyStage: parsedData.companyStage || '',
  salaryRange: parsedData.salaryRange || '',
  qualifications: parsedData.qualifications || [],
  benefits: parsedData.benefits || [],
  techStack: parsedData.techStack || [],
  interviewFocus: parsedData.interviewFocus || [],
 });

 const handleInputChange = (field: keyof CreateJobProfileData, value: any) => {
  setFormData((prev) => ({ ...prev, [field]: value }));
 };

 const addSkill = () => {
  if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
   setFormData((prev) => ({
    ...prev,
    skills: [...prev.skills, newSkill.trim()],
   }));
   setNewSkill('');
  }
 };

 const removeSkill = (skillToRemove: string) => {
  setFormData((prev) => ({
   ...prev,
   skills: prev.skills.filter((skill) => skill !== skillToRemove),
  }));
 };

 const handleSave = () => {
  // Combine job description with extra context if provided
  const finalJobDescription = extraContext.trim()
   ? `${formData.jobDescription}\n\nAdditional Context:\n${extraContext}`
   : formData.jobDescription;

  onSave({
   ...formData,
   jobDescription: finalJobDescription,
  });
 };

 const isFormValid =
  formData.jobTitle.trim() &&
  formData.companyName.trim() &&
  formData.jobDescription.trim();

 return (
  <div className='space-y-6'>
   {/* Core Job Information */}
   <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
    <CardHeader>
     <CardTitle className='font-mono uppercase tracking-wider text-xl'>
      Core Job Information
     </CardTitle>
     <CardDescription className='font-mono text-xs'>
      Essential details about the position
     </CardDescription>
    </CardHeader>
    <CardContent className='space-y-4'>
     <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
      <div className='space-y-2'>
       <Label
        htmlFor='jobTitle'
        className='font-mono uppercase text-xs text-muted-foreground'
       >
        Job Title *
       </Label>
       <Input
        id='jobTitle'
        value={formData.jobTitle}
        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
        placeholder='e.g. Senior Frontend Developer'
        className='rounded-none font-mono bg-secondary/5 border-border'
       />
      </div>

      <div className='space-y-2'>
       <Label
        htmlFor='companyName'
        className='font-mono uppercase text-xs text-muted-foreground'
       >
        Company Name *
       </Label>
       <Input
        id='companyName'
        value={formData.companyName}
        onChange={(e) => handleInputChange('companyName', e.target.value)}
        placeholder='e.g. TechCorp Inc.'
        className='rounded-none font-mono bg-secondary/5 border-border'
       />
      </div>
     </div>

     <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
      <div className='space-y-2'>
       <Label
        htmlFor='experienceLevel'
        className='font-mono uppercase text-xs text-muted-foreground'
       >
        Experience Level
       </Label>
       <Select
        value={formData.experienceLevel}
        onValueChange={(value) => handleInputChange('experienceLevel', value)}
       >
        <SelectTrigger className='rounded-none font-mono bg-secondary/5 border-border'>
         <SelectValue />
        </SelectTrigger>
        <SelectContent>
         <SelectItem value='entry'>Entry Level</SelectItem>
         <SelectItem value='mid'>Mid Level</SelectItem>
         <SelectItem value='senior'>Senior Level</SelectItem>
         <SelectItem value='lead'>Lead/Principal</SelectItem>
         <SelectItem value='executive'>Executive</SelectItem>
        </SelectContent>
       </Select>
      </div>

      <div className='space-y-2'>
       <Label
        htmlFor='interviewType'
        className='font-mono uppercase text-xs text-muted-foreground'
       >
        Interview Type
       </Label>
       <Select
        value={formData.interviewType}
        onValueChange={(value) => handleInputChange('interviewType', value)}
       >
        <SelectTrigger className='rounded-none font-mono bg-secondary/5 border-border'>
         <SelectValue />
        </SelectTrigger>
        <SelectContent>
         <SelectItem value='technical'>Technical</SelectItem>
         <SelectItem value='behavioral'>Behavioral</SelectItem>
         <SelectItem value='mixed'>Mixed</SelectItem>
         <SelectItem value='case_study'>Case Study</SelectItem>
        </SelectContent>
       </Select>
      </div>

      <div className='space-y-2'>
       <Label
        htmlFor='industry'
        className='font-mono uppercase text-xs text-muted-foreground'
       >
        Industry
       </Label>
       <Input
        id='industry'
        value={formData.industry}
        onChange={(e) => handleInputChange('industry', e.target.value)}
        placeholder='e.g. Technology, Finance'
        className='rounded-none font-mono bg-secondary/5 border-border'
       />
      </div>
     </div>
    </CardContent>
   </Card>

   {/* Job Description */}
   <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
    <CardHeader>
     <CardTitle className='font-mono uppercase tracking-wider text-xl'>
      Job Description
     </CardTitle>
     <CardDescription className='font-mono text-xs'>
      Detailed description of the role and responsibilities
     </CardDescription>
    </CardHeader>
    <CardContent className='space-y-4'>
     <div className='space-y-2'>
      <Label
       htmlFor='jobDescription'
       className='font-mono uppercase text-xs text-muted-foreground'
      >
       Job Description *
      </Label>
      <Textarea
       id='jobDescription'
       value={formData.jobDescription}
       onChange={(e) => handleInputChange('jobDescription', e.target.value)}
       placeholder='Detailed job description...'
       rows={6}
       className='rounded-none font-mono bg-secondary/5 border-border'
      />
     </div>

     <div className='space-y-2'>
      <Label
       htmlFor='companyInfo'
       className='font-mono uppercase text-xs text-muted-foreground'
      >
       Company Information
      </Label>
      <Textarea
       id='companyInfo'
       value={formData.companyInfo}
       onChange={(e) => handleInputChange('companyInfo', e.target.value)}
       placeholder='Information about the company...'
       rows={3}
       className='rounded-none font-mono bg-secondary/5 border-border'
      />
     </div>
    </CardContent>
   </Card>

   {/* Skills */}
   <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
    <CardHeader>
     <CardTitle className='font-mono uppercase tracking-wider text-xl'>
      Required Skills
     </CardTitle>
     <CardDescription className='font-mono text-xs'>
      Technical skills and competencies needed for this role
     </CardDescription>
    </CardHeader>
    <CardContent className='space-y-4'>
     <div className='flex space-x-2'>
      <Input
       value={newSkill}
       onChange={(e) => setNewSkill(e.target.value)}
       placeholder='Add a skill...'
       onKeyPress={(e) => e.key === 'Enter' && addSkill()}
       className='rounded-none font-mono bg-secondary/5 border-border'
      />
      <Button
       onClick={addSkill}
       variant='outline'
       size='sm'
       className='rounded-none'
      >
       <Plus className='w-4 h-4' />
      </Button>
     </div>

     <div className='flex flex-wrap gap-2'>
      {formData.skills.map((skill, index) => (
       <Badge
        key={index}
        variant='secondary'
        className='flex items-center gap-1 rounded-none font-mono border-primary/20 bg-primary/5 text-primary hover:bg-primary/10'
       >
        {skill}
        <button
         onClick={() => removeSkill(skill)}
         className='ml-1 hover:text-red-500 transition-colors p-0.5'
        >
         <X className='w-3 h-3' />
        </button>
       </Badge>
      ))}
     </div>
    </CardContent>
   </Card>

   {/* Additional Parsed Information */}
   {(additionalInfo.techStack.length > 0 ||
    additionalInfo.qualifications.length > 0 ||
    additionalInfo.benefits.length > 0 ||
    additionalInfo.interviewFocus.length > 0) && (
    <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
     <CardHeader>
      <CardTitle className='font-mono uppercase tracking-wider text-xl'>
       Additional Information
      </CardTitle>
      <CardDescription className='font-mono text-xs'>
       Extra details extracted from the job posting
      </CardDescription>
     </CardHeader>
     <CardContent className='space-y-4'>
      {additionalInfo.yearsOfExperience && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Years of Experience
        </Label>
        <p className='text-sm text-foreground/80 font-mono'>
         {additionalInfo.yearsOfExperience}
        </p>
       </div>
      )}

      {additionalInfo.workModel && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Work Model
        </Label>
        <p className='text-sm text-foreground/80 font-mono capitalize'>
         {additionalInfo.workModel}
        </p>
       </div>
      )}

      {additionalInfo.salaryRange && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Salary Range
        </Label>
        <p className='text-sm text-foreground/80 font-mono'>
         {additionalInfo.salaryRange}
        </p>
       </div>
      )}

      {additionalInfo.techStack.length > 0 && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Tech Stack
        </Label>
        <div className='flex flex-wrap gap-1 mt-1'>
         {additionalInfo.techStack.map((tech, index) => (
          <Badge
           key={index}
           variant='outline'
           className='text-[10px] rounded-none font-mono uppercase bg-secondary/5 border-border'
          >
           {tech}
          </Badge>
         ))}
        </div>
       </div>
      )}

      {additionalInfo.qualifications.length > 0 && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Qualifications
        </Label>
        <ul className='text-xs text-muted-foreground mt-1 space-y-1 font-mono'>
         {additionalInfo.qualifications.map((qual, index) => (
          <li key={index}>&gt; {qual}</li>
         ))}
        </ul>
       </div>
      )}

      {additionalInfo.benefits.length > 0 && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Benefits
        </Label>
        <div className='flex flex-wrap gap-1 mt-1'>
         {additionalInfo.benefits.map((benefit, index) => (
          <Badge
           key={index}
           variant='outline'
           className='text-[10px] rounded-none font-mono uppercase bg-secondary/5 border-border'
          >
           {benefit}
          </Badge>
         ))}
        </div>
       </div>
      )}

      {additionalInfo.interviewFocus.length > 0 && (
       <div>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Interview Focus Areas
        </Label>
        <div className='flex flex-wrap gap-1 mt-1'>
         {additionalInfo.interviewFocus.map((focus, index) => (
          <Badge
           key={index}
           variant='outline'
           className='text-[10px] rounded-none font-mono uppercase bg-secondary/5 border-border'
          >
           {focus}
          </Badge>
         ))}
        </div>
       </div>
      )}
     </CardContent>
    </Card>
   )}

   {/* Extra Context */}
   <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
    <CardHeader>
     <CardTitle className='font-mono uppercase tracking-wider text-xl'>
      Additional Context
     </CardTitle>
     <CardDescription className='font-mono text-xs'>
      Add any extra information or notes about this position
     </CardDescription>
    </CardHeader>
    <CardContent>
     <Textarea
      value={extraContext}
      onChange={(e) => setExtraContext(e.target.value)}
      placeholder='Any additional context, notes, or specific areas you want to focus on during interview preparation...'
      rows={4}
      className='rounded-none font-mono bg-secondary/5 border-border'
     />
    </CardContent>
   </Card>

   {/* Action Buttons */}
   <div className='flex space-x-3'>
    <Button
     onClick={handleSave}
     disabled={!isFormValid}
     className='flex-1 rounded-none font-mono uppercase tracking-widest'
    >
     CONTINUE_TO_SAVE
    </Button>
    <Button
     variant='outline'
     onClick={onBack}
     className='rounded-none font-mono uppercase'
    >
     Back to URL
    </Button>
   </div>
  </div>
 );
}
